import React from 'react';

import { Navigate } from 'react-router';

import { FullscreenLoader } from '@hrlogics/react-components';

import { appRoutes } from '@clearverify/constants';

import { CompanyType } from 'src/api';
import { SignupConsumerFormStepId } from 'src/constants/auth';
import { useAuthSignupConsumer } from 'src/hooks/auth';
import { useInviteParamId } from 'src/hooks/invite';
import {
  useEmployeeBasicInfoFormHandler,
  usePinFormSubmitHandler,
  useSsnFormSubmitHandler,
} from 'src/hooks/signup';
import { SxProp } from 'src/types/mui';

import { AuthPaperBox } from '../AuthPaperBox';

import {
  ConsumerAttestationsForm,
  ConsumerBasicInfoForm,
  ConsumerReviewDataForm,
  ConsumerVerificationPreferenceForm,
  PinForm,
  SsnForm,
} from './components';

export const SignupConsumerByPeoPayrollProviderPageForm: React.FC = () => {
  const inviteId = useInviteParamId();

  const {
    peoPayrollProviderInfo,
    updateFormData,
    goToNextStep,
    loading,
    employeeSignupToken,
    setEmployeeSignupToken,
    formStepperState,
  } = useAuthSignupConsumer();

  const boxStyles: SxProp | undefined = React.useMemo(() => {
    if (formStepperState.currentStepId === SignupConsumerFormStepId.consumerReviewData) {
      return consumerReviewDataContainerStyles;
    }
    return undefined;
  }, [formStepperState.currentStepId]);

  const { handleSsnFormSubmit } = useSsnFormSubmitHandler({
    companyId: peoPayrollProviderInfo?.id || '',
    companyType: CompanyType.PeoPayrollProvider,
    updateFormData,
    setEmployeeSignupToken,
    goToNextStep,
  });
  const { handlePinFormSubmit } = usePinFormSubmitHandler({
    companyId: peoPayrollProviderInfo?.id || '',
    companyType: CompanyType.PeoPayrollProvider,
    updateFormData,
    setEmployeeSignupToken,
    goToNextStep,
  });
  const { handleEmployeeBasicInfoFormSubmit } = useEmployeeBasicInfoFormHandler({
    inviteId,
    employeeSignupToken,
    updateFormData,
    setEmployeeSignupToken,
    goToNextStep,
  });

  const renderForm = () => {
    switch (formStepperState.currentStepId) {
      case SignupConsumerFormStepId.ssn:
        return (
          <SsnForm
            onSubmit={handleSsnFormSubmit}
            companyName={peoPayrollProviderInfo?.displayName}
          />
        );
      case SignupConsumerFormStepId.pin:
        return (
          <PinForm
            onSubmit={handlePinFormSubmit}
            companyName={peoPayrollProviderInfo?.displayName}
            customHint={peoPayrollProviderInfo?.pinHint}
          />
        );
      case SignupConsumerFormStepId.consumerBasicInfo:
        return (
          <ConsumerBasicInfoForm
            onSubmit={handleEmployeeBasicInfoFormSubmit}
            displayName={peoPayrollProviderInfo?.displayName}
            loading={loading}
            signUpMethod={peoPayrollProviderInfo?.signUpMethod}
          />
        );
      case SignupConsumerFormStepId.consumerAttestations:
        return <ConsumerAttestationsForm />;
      case SignupConsumerFormStepId.consumerVerificationPreference:
        return <ConsumerVerificationPreferenceForm />;
      case SignupConsumerFormStepId.consumerReviewData:
        return <ConsumerReviewDataForm />;
      default:
        return null;
    }
  };

  if (loading) {
    return <FullscreenLoader sx={{ height: 'unset' }} />;
  }

  if (!peoPayrollProviderInfo) {
    return <Navigate to={appRoutes.auth.index} />;
  }

  return (
    <AuthPaperBox
      sx={boxStyles}
      variant={peoPayrollProviderInfo.logo?.downloadUrl ? 'withCompanyLogo' : 'default'}
      companyLogoUrl={peoPayrollProviderInfo.logo?.downloadUrl}
    >
      {renderForm()}
    </AuthPaperBox>
  );
};

const consumerReviewDataContainerStyles: SxProp = {
  maxWidth: 'none',
  minWidth: 540,
};
